import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';

export const AiAgentsService = () => {
  const whatWeBuild = [
    {
      title: 'Lead Capture Agents',
      description: 'AI chat agents that live on your site and ask the right questions to capture qualified leads.'
    },
    {
      title: 'Appointment Agents',
      description: 'Automations that book calls and meetings directly into your team\'s calendars.'
    },
    {
      title: 'Follow-up Agents',
      description: 'Email and messaging sequences that nurture leads based on their behavior and inputs.'
    },
    {
      title: 'Internal Ops Automations',
      description: 'Workflows that sync data between your CRM, marketing tools, and internal systems.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery',
      description: 'Map your current funnel and identify bottlenecks where automation can help.'
    },
    {
      step: '02',
      title: 'Design',
      description: 'Specify agent behaviors, rules, and safeguards to ensure quality interactions.'
    },
    {
      step: '03',
      title: 'Build & Integrate',
      description: 'Connect with your existing tools (CRM, email, calendar, etc.).'
    },
    {
      step: '04',
      title: 'Test & Refine',
      description: 'Optimize performance and guardrails based on real interactions.'
    }
  ];

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <section className="pt-24 lg:pt-32 pb-12 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 gradient-hero opacity-5" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-6 bg-accent/10 text-accent border-accent/20">
                <Sparkles className="w-3 h-3 mr-1" />
                Automation
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6">
                AI Agents & Automations:{' '}
                <span className="text-gradient">Convert Traffic into Customers</span> on Autopilot
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground mb-8">
                Build agents and automations that generate content, spin up programmatic SEO pages around trending GEO-aligned keywords, and turn that traffic into qualified opportunities on autopilot.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="gradient-primary text-primary-foreground shadow-primary">
                    Discuss Your Project
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/free-audit">
                  <Button size="lg" variant="outline">
                    Get Free Audit
                  </Button>
                </Link>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1761305135319-70b8dbde3525?w=800&h=600&fit=crop"
                alt="AI Agents Service"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What We Build */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-12 text-center">
            What We Build
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {whatWeBuild.map((item, index) => (
              <Card key={index} className="border-border hover:border-primary/50 transition-colors">
                <CardContent className="pt-6">
                  <h3 className="font-display font-semibold text-lg mb-3">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Now */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6 text-center">
            Why AI Agents Now?
          </h2>
          <Card className="border-primary/50 bg-primary/5">
            <CardContent className="pt-6">
              <p className="text-foreground leading-relaxed mb-4">
                AI agents and automations let you scale your response capacity without hiring more people. They're especially powerful when combined with GEO/AIO: more people discover you through AI search, and your agents make sure you capture that interest immediately.
              </p>
              <p className="text-foreground leading-relaxed">
                Unlike traditional chatbots that follow rigid scripts, modern AI agents can understand context, ask intelligent follow-up questions, and adapt their responses based on user behavior.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-12 text-center">
            Our Process
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="text-5xl font-display font-bold text-primary/20 mb-4">
                  {item.step}
                </div>
                <h3 className="font-display font-semibold text-lg mb-3">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-12 text-center">
            Common Use Cases
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-border">
              <CardContent className="pt-6">
                <h3 className="font-display font-semibold text-lg mb-3">Lead Qualification</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Automatically ask qualifying questions, score leads, and route high-value prospects to sales immediately.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-muted-foreground">24/7 availability</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-muted-foreground">Instant response</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="pt-6">
                <h3 className="font-display font-semibold text-lg mb-3">Appointment Booking</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Let prospects book demos or consultations directly without back-and-forth emails.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-muted-foreground">Calendar sync</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-muted-foreground">Automatic reminders</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="pt-6">
                <h3 className="font-display font-semibold text-lg mb-3">Follow-up Nurturing</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Automatically nurture leads with personalized sequences based on their interests and actions.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-muted-foreground">Behavioral triggers</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-muted-foreground">Personalized content</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-8 text-center">
            AI Agents & Automations FAQs
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                What&apos;s the difference between an AI agent and a chatbot?
              </h3>
              <p className="text-muted-foreground">
                Traditional chatbots follow rigid scripts. Our agents can understand intent, access your data, trigger workflows, and hand off to humans when it makes sense. They behave more like teammates than static widgets.
              </p>
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                Where do these agents live?
              </h3>
              <p className="text-muted-foreground">
                We can deploy agents on your website, inside chat tools, in CRMs, or as backend automations that run in the background. The exact placement depends on where AI visibility is already sending you traffic and where your team spends time.
              </p>
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                How do you keep automations on-brand and compliant?
              </h3>
              <p className="text-muted-foreground">
                We start with guardrails: brand voice, approval flows, and escalation rules. For regulated industries, we work with your legal or compliance team to define what agents can and cannot say or trigger.
              </p>
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                How long does it take to launch an AI agent or automation?
              </h3>
              <p className="text-muted-foreground">
                Simple agents and workflows can often go live within a few weeks, while more complex, multi-system automations take longer. We&apos;ll prioritize quick wins first so you start seeing impact while we build deeper capabilities.
              </p>
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                What tools and platforms do you typically integrate with?
              </h3>
              <p className="text-muted-foreground">
                We regularly integrate with common CRMs, marketing automation tools, calendar systems, data warehouses, and custom stacks. The goal is to make agents feel native to your existing workflows, not bolt-ons your team has to fight with.
              </p>
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">
                How do you measure the success of agents and automations?
              </h3>
              <p className="text-muted-foreground">
                We look at response times, conversion rates, meetings booked, pipeline influenced, and time saved for your team. Together we&apos;ll define the right KPIs up front so you can clearly see the ROI of each automation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6">
            Automate Your Growth Funnel
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Get a free GEO/AIO audit and we&apos;ll include custom automation and programmatic SEO ideas tailored to your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/free-audit">
              <Button size="lg" className="gradient-primary text-primary-foreground shadow-primary">
                Get Free Audit + Automation Ideas <span className="font-semibold">(Valued at $2,000+)</span>
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline">
                Discuss Custom Project
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
